import type { Viewport } from "pixi-viewport";
import {
  Application,
  FederatedPointerEvent,
  Graphics,
  Sprite,
  Texture,
} from "pixi.js";
import { MosaicContainer, tileWidth } from "./particle";

export class Brush {
  private sprite: Sprite;
  private colorIndex?: number;
  private colorHex = 0x666666;
  private x = 0;
  private y = 0;

  constructor(
    private size: number,
    private app: Application,
    private vp: Viewport,
    private pc: MosaicContainer
  ) {
    // --- 1) Build the Graphics once (with halo + ring) ---
    const g = new Graphics();

    const radius = this.size * 0.75; // base radius in world units
    const baseColor = 0xffffff; // draw everything in white, tint later

    // halo layers
    const haloLayers = [
      { r: radius * 1.0, a: 0.06 },
      { r: radius * 0.75, a: 0.1 },
      { r: radius * 0.5, a: 0.16 },
      { r: radius * 0.28, a: 0.22 },
    ];
    for (const { r, a } of haloLayers) {
      g.circle(0, 0, r).fill({ color: baseColor, alpha: a });
    }

    // outer stroke (kept white, will tint)
    g.circle(0, 0, radius * 1.5).stroke({
      width: 3,
      color: baseColor,
      alpha: 1,
      alignment: 0.5,
      cap: "round",
      join: "round",
    });

    // --- 2) Bake into a texture ---
    const tex: Texture = this.app.renderer.generateTexture(g);
    g.destroy(true);

    // --- 3) Wrap in a Sprite ---
    this.sprite = new Sprite(tex);
    this.sprite.anchor.set(0.5);
    this.sprite.zIndex = 2000;
    this.sprite.visible = false;
    this.vp.addChild(this.sprite);

    // pointer tracking
    this.vp.on("pointerdown", this.onMove, this);
    this.vp.on("pointermove", this.onMove, this);
    this.vp.on("zoomed", this.redraw, this);
  }

  show(colourIndex?: number) {
    this.colorIndex = colourIndex;
    if (colourIndex !== undefined) {
      this.colorHex = this.pc.getPaletteColorByIndex(colourIndex);
    }
    this.sprite.visible = true;
    this.redraw();
  }

  hide() {
    this.sprite.visible = false;
  }

  destroy() {
    this.vp.off("pointerdown", this.onMove);
    this.vp.off("pointermove", this.onMove);
    this.vp.off("zoomed", this.redraw);
    this.sprite.destroy(true);
  }

  private onMove(e: FederatedPointerEvent) {
    const world = this.vp.toWorld(e.global);
    this.x = world.x;
    this.y = world.y;
    this.redraw();
  }

  private redraw() {
    if (!this.sprite.visible || this.colorIndex === undefined) return;

    const scale = this.vp.scale?.x ?? 1;
    this.sprite.position.set(this.x, this.y);
    this.sprite.scale.set(1 / scale); // constant screen size
    this.sprite.tint = this.colorHex;
  }

  public hitArea(): Array<{ x: number; y: number }> {
    const results: Array<{ x: number; y: number }> = [];
    const radius = this.size * 0.5;
    const tileSize = tileWidth;
    const mosaicBorder = this.pc.x;

    const minX = this.x - radius - mosaicBorder;
    const maxX = minX + this.size;
    const minY = this.y - radius - mosaicBorder;
    const maxY = minY + this.size;

    const minTileX = Math.floor(minX / tileSize);
    const maxTileX = Math.floor(maxX / tileSize);
    const minTileY = Math.floor(minY / tileSize);
    const maxTileY = Math.floor(maxY / tileSize);

    for (let ty = minTileY; ty <= maxTileY; ty++) {
      for (let tx = minTileX; tx <= maxTileX; tx++) {
        results.push({ x: tx, y: ty });
      }
    }
    return results;
  }

  applyHitbox(action: (xi: number, yi: number) => boolean): boolean {
    const area = this.hitArea();
    let hit = false;
    for (const { x, y } of area) {
      if (action(x, y)) {
        hit = true;
      }
    }
    return hit;
  }
}
